import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEffect } from 'react';

export interface PublicMenuData {
  branch: any;
  layout: any;
  categories: any[];
  menuItems: any[];
  variants: any[];
  popOffer: any | null;
  subscription: {
    module_online_ordering: boolean;
    online_ordering_tier: 'none' | 'basic' | 'advanced' | 'enterprise';
    branding_tier: 'none' | 'basic' | 'advanced' | 'enterprise' | 'white_label';
  };
}

// Type for the global prefetch promise
declare global {
  interface Window {
    __PREFETCH_MENU_DATA__?: Promise<PublicMenuData | null>;
  }
}

export const usePublicMenuFullData = (slug: string | undefined) => {
  const queryClient = useQueryClient();
  
  // Try to use prefetched data from index.html
  useEffect(() => {
    if (slug && window.__PREFETCH_MENU_DATA__) {
      window.__PREFETCH_MENU_DATA__.then((data) => {
        if (data) {
          // Seed the query cache with prefetched data
          queryClient.setQueryData(['public-menu-full', slug], data);
        }
        // Clear the global to prevent memory leaks
        delete window.__PREFETCH_MENU_DATA__;
      });
    }
  }, [slug, queryClient]);
  
  return useQuery({
    queryKey: ['public-menu-full', slug],
    queryFn: async () => {
      if (!slug) return null;

      const { data, error } = await supabase
        .rpc('get_public_menu_full_data', { p_slug: slug });

      if (error) {
        console.error('Error fetching public menu data:', error);
        throw error;
      }

      return data as PublicMenuData;
    },
    enabled: !!slug,
    staleTime: 0, // Always consider data stale so a background refetch runs on every mount
    gcTime: 1000 * 60 * 5, // Keep in memory for 5 minutes (fast re-renders within same session)
    refetchOnWindowFocus: true, // Refetch when customer returns to tab (catches owner price updates)
    refetchOnMount: true, // Always refetch when the menu page is opened
    // Show previous data immediately while the fresh fetch runs in background
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Prefetch public menu data - call this as early as possible
 * Can be called from index.html script or route loader
 */
export const prefetchPublicMenuData = async (slug: string) => {
  if (!slug) return null;
  
  try {
    const { data, error } = await supabase
      .rpc('get_public_menu_full_data', { p_slug: slug });
    
    if (error) throw error;
    return data;
  } catch (e) {
    console.error('Prefetch failed:', e);
    return null;
  }
};
